import './ChangeEvents.scss';

import { ApiRequests } from '@security/ApiRequests';
import { CHANGE_EVENT_PREFERENCES } from '@sonar-software/sonar-ts-constants';
import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { MonitoredObjects } from './MonitoredObjects';
import { SanitizedHtml } from '@ui/general';
import { When } from 'react-if';

import React, { useCallback } from 'react';
import { useAction, useAsyncState, useOnMount } from '@hooks';

const monitoredObjectSelectors = createAsyncSelectors((state) => state.core.dataSecurity.monitoredObjects.get);
const usageSelectors = createAsyncSelectors((state) => state.core.dataSecurity.usage.get);

export interface ChangeEventsProps {
	isEnabled: boolean | undefined | null;
	isLoading: boolean;
}

export const ChangeEvents: React.FC<ChangeEventsProps> = ({ isEnabled, isLoading }) => {
	const { data: monitoredObjects, isRequested: isMonitoredObjectsRequested } =
		useAsyncState(monitoredObjectSelectors);
	const { data: usage, isRequested: isUsageRequested } = useAsyncState(usageSelectors);
	const hasData = Boolean(monitoredObjects?.totalCount);

	const loadObjects = useAction(coreActions.requestGetCoreDataSecurityMonitoredObjects);
	const loadUsage = useAction(coreActions.requestGetCoreDataSecurityUsage);

	const handleRefresh = useCallback(() => {
		loadObjects();
		loadUsage();
	}, [ loadObjects, loadUsage ]);

	useOnMount(() => {
		loadObjects();
		loadUsage();
	});

	return (
		<div className={ hasData ? 'monitored-objects' : '' }>
			<MonitoredObjects
				isEnabled={ Boolean(isEnabled) }
				loading={ isMonitoredObjectsRequested || isLoading }
				monitoredObjects={ monitoredObjects }
				onRefresh={ handleRefresh }
				refreshing={ isMonitoredObjectsRequested }
				salesforceUrl={ usage?.sourceUrl } />
			<When condition={ hasData && isEnabled }>
				<LoadingSpinner spinning={ isUsageRequested }>
					<div className='data-security-api-requests'>
						{ usage?.dailyDeliveredPlatformEvents &&
							<ApiRequests
								header={ CHANGE_EVENT_PREFERENCES.CHANGE_EVENTS_DAILY }
								maxCalls={ usage.dailyDeliveredPlatformEvents.max }
								tooltip={ <SanitizedHtml
									as='span'
									html={ CHANGE_EVENT_PREFERENCES.TOOLTIP_DAILY } /> }
								usedCalls={
									usage.dailyDeliveredPlatformEvents.max -
									usage.dailyDeliveredPlatformEvents.remaining
								} /> }
						{ usage?.monthlyPlatformEventsUsageEntitlement &&
							<ApiRequests
								header={ CHANGE_EVENT_PREFERENCES.CHANGE_EVENTS_MONTHLY }
								maxCalls={ usage.monthlyPlatformEventsUsageEntitlement.max }
								tooltip={ <SanitizedHtml
									as='span'
									html={ CHANGE_EVENT_PREFERENCES.TOOLTIP_MONTHLY } /> }
								usedCalls={
									usage.monthlyPlatformEventsUsageEntitlement.max -
									usage.monthlyPlatformEventsUsageEntitlement.remaining
								} /> }
					</div>
				</LoadingSpinner>
			</When>
		</div>
	);
};
